import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { WorkspaceBidderContext } from 'features/globalSetup/contexts/WorkspaceBidderContext'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { toDTO } from './api/mapper'
import { UPDATE_WORKSPACE_BIDDER } from './api/mutation'

import type { Data, Variables } from './api/types'
import type { UseUpdateWorkspaceBidder, UseUpdateWorkspaceBidderProps } from './types'
import type { EditSchema } from 'features/globalSetup/forms/Prebid/Bidders/schemas/editSchema'
import type { FieldErrors } from 'react-hook-form'

export const useUpdateWorkspaceBidder = ({
  bidderCode,
  id,
}: UseUpdateWorkspaceBidderProps): UseUpdateWorkspaceBidder => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.list.updateEnabled.action',
  })
  const { setLoading } = useContext(WorkspaceBidderContext)
  const [errors, setErrors] = useState<FieldErrors<EditSchema>>({})
  const { enqueueSnackbar } = useSnackbar()

  const tParams = {
    action: 'updated',
    bidderCode,
  } as const

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_WORKSPACE_BIDDER,
    onCompleted: async ({ updateWorkspaceBidder: { errors } }: Data) => {
      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('error', tParams), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('success', tParams), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['getWorkspaceBidder'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const update = (data: EditSchema): void => {
    mutateAsync({
      input: toDTO({
        from: data,
        workspaceBidderId: id,
      }),
    })
  }

  return { errors, update }
}
